import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Gauge, Plus, Loader2, Trash2, Edit2, AlertTriangle, TrendingUp,
  TrendingDown, Target, Calendar, Euro, Check, X, AlertCircle
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { DatePickerFr } from "../../ui/date-picker-fr";
import {
  MILEAGE_SOURCE_OPTIONS,
  MILEAGE_SOURCE_LABELS,
  MILEAGE_STATUS_COLORS,
  formatMileage,
  formatCurrency,
  formatDate,
} from "../../../lib/fleet-constants";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Legend
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MileageTab({ vehicle, onUpdate }) {
  const [readings, setReadings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, reading: null });
  
  const [form, setForm] = useState({
    reading_date: new Date().toISOString().split("T")[0],
    mileage: "",
    source: "declarative",
    notes: "",
    force_override: false
  });
  
  const token = localStorage.getItem("token");

  const fetchMileage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicle.id}/mileage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadings(response.data.readings || []);
      setStats(response.data.stats || null);
    } catch (err) {
      console.error("Error fetching mileage:", err);
      toast.error("Erreur lors du chargement des relevés");
    } finally {
      setLoading(false);
    }
  }, [vehicle.id, token]);

  useEffect(() => {
    fetchMileage();
  }, [fetchMileage]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      reading_date: new Date().toISOString().split("T")[0],
      mileage: "",
      source: "declarative",
      notes: "",
      force_override: false
    });
  };

  const handleSubmit = async () => {
    if (!form.mileage || parseInt(form.mileage) <= 0) {
      toast.error("Veuillez saisir un kilométrage valide");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/fleet/vehicles/${vehicle.id}/mileage`,
        {
          reading_date: form.reading_date,
          mileage: parseInt(form.mileage),
          source: form.source,
          notes: form.notes || null,
          force_override: form.force_override
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Relevé enregistré");
      setShowAddModal(false);
      resetForm();
      fetchMileage();
      onUpdate?.();
    } catch (err) {
      console.error("Error creating mileage reading:", err);
      const detail = err.response?.data?.detail;
      if (detail && detail.includes("inférieur")) {
        toast.error("Kilométrage invalide", { description: detail });
      } else {
        toast.error("Erreur", { description: detail || "Impossible d'enregistrer le relevé" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.reading) return;
    
    try {
      await axios.delete(
        `${API}/fleet/vehicles/${vehicle.id}/mileage/${deleteDialog.reading.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Relevé supprimé");
      setDeleteDialog({ open: false, reading: null });
      fetchMileage();
      onUpdate?.();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Prepare chart data
  const getChartData = () => {
    if (!readings.length || !stats) return [];
    
    const sortedReadings = [...readings].sort((a, b) => 
      new Date(a.reading_date) - new Date(b.reading_date)
    );
    
    const startDate = stats.contract_start_date ? new Date(stats.contract_start_date) : new Date(sortedReadings[0]?.reading_date);
    const endDate = stats.contract_end_date ? new Date(stats.contract_end_date) : new Date();
    const totalAllowed = stats.contract_total_allowed || 0;
    const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    const dailyAllowance = totalDays > 0 ? totalAllowed / totalDays : 0;
    
    // Create data points
    const data = [];
    
    // Add theoretical line points
    const theoreticalStart = { 
      date: startDate.toISOString().split("T")[0], 
      theoretical: 0 
    };
    const theoreticalEnd = { 
      date: endDate.toISOString().split("T")[0], 
      theoretical: totalAllowed 
    };
    
    // Add readings
    sortedReadings.forEach(r => {
      const readingDate = new Date(r.reading_date);
      const daysSinceStart = (readingDate - startDate) / (1000 * 60 * 60 * 24);
      const theoreticalAtDate = Math.round(dailyAllowance * daysSinceStart);
      
      data.push({
        date: r.reading_date,
        actual: r.mileage,
        theoretical: theoreticalAtDate,
        reading: r
      });
    });
    
    // Sort by date
    data.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return data;
  };

  const chartData = getChartData();

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  const StatusCard = ({ icon: Icon, label, value, subValue, status }) => (
    <Card className={`${
      status === "red" ? "border-red-200 bg-red-50" :
      status === "orange" ? "border-orange-200 bg-orange-50" :
      "border-slate-200"
    }`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className={`text-xl font-bold mt-1 ${
              status === "red" ? "text-red-700" :
              status === "orange" ? "text-orange-700" :
              "text-slate-900"
            }`}>{value}</p>
            {subValue && (
              <p className="text-sm text-slate-500 mt-0.5">{subValue}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            status === "red" ? "bg-red-500" :
            status === "orange" ? "bg-orange-500" :
            "bg-indigo-500"
          }`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {stats && stats.status !== "green" && (
            <Badge className={MILEAGE_STATUS_COLORS[stats.status]}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              {stats.status === "red" ? "Dépassement prévu" : "Attention"}
            </Badge>
          )}
        </div>
        <Button onClick={() => setShowAddModal(true)} data-testid="add-mileage-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau relevé
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatusCard
            icon={Gauge}
            label="Kilométrage actuel"
            value={formatMileage(stats.current_mileage)}
            subValue={stats.last_reading_date ? `Relevé le ${formatDate(stats.last_reading_date)}` : null}
          />
          
          <StatusCard
            icon={Target}
            label="Km théorique à date"
            value={formatMileage(stats.theoretical_mileage)}
            subValue={stats.contract_annual_allowed ? `${formatMileage(stats.contract_annual_allowed)}/an` : null}
          />
          
          <StatusCard
            icon={stats.mileage_difference > 0 ? TrendingUp : TrendingDown}
            label="Écart"
            value={stats.mileage_difference !== null ? `${stats.mileage_difference > 0 ? "+" : ""}${formatMileage(stats.mileage_difference)}` : "-"}
            subValue={stats.mileage_difference_percent !== null ? `${stats.mileage_difference_percent > 0 ? "+" : ""}${stats.mileage_difference_percent}%` : null}
            status={stats.mileage_difference > 0 ? (stats.mileage_difference_percent > 10 ? "red" : "orange") : null}
          />
          
          <StatusCard
            icon={Euro}
            label="Coût dépassement prévu"
            value={stats.projected_excess_cost ? formatCurrency(stats.projected_excess_cost) : "-"}
            subValue={stats.projected_excess ? `${formatMileage(stats.projected_excess)} en excès` : "Aucun dépassement"}
            status={stats.projected_excess > 0 ? "red" : null}
          />
        </div>
      )}
      
      {/* Alert Message */}
      {stats && stats.status !== "green" && (
        <Card className={stats.status === "red" ? "border-red-200 bg-red-50" : "border-orange-200 bg-orange-50"}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 ${stats.status === "red" ? "text-red-600" : "text-orange-600"}`} />
              <div>
                <p className={`font-medium ${stats.status === "red" ? "text-red-800" : "text-orange-800"}`}>
                  {stats.status_message}
                </p>
                {stats.projected_excess_cost > 0 && (
                  <p className="text-sm mt-1 text-slate-600">
                    À ce rythme, le surcoût à la restitution sera estimé à <strong>{formatCurrency(stats.projected_excess_cost)}</strong>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Chart */}
      {chartData.length > 0 && stats?.contract_total_allowed && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Évolution du kilométrage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(v) => formatDate(v)}
                    fontSize={12}
                    tick={{ fill: '#64748b' }}
                  />
                  <YAxis 
                    tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                    fontSize={12}
                    tick={{ fill: '#64748b' }}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      formatMileage(value),
                      name === "actual" ? "Réel" : "Théorique"
                    ]}
                    labelFormatter={(label) => formatDate(label)}
                    contentStyle={{ 
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend 
                    formatter={(value) => value === "actual" ? "Kilométrage réel" : "Kilométrage autorisé"}
                  />
                  
                  {/* Theoretical line */}
                  <Line
                    type="monotone"
                    dataKey="theoretical"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="theoretical"
                  />
                  
                  {/* Actual line */}
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#6366f1"
                    strokeWidth={3}
                    dot={{ fill: '#6366f1', strokeWidth: 2 }}
                    name="actual"
                  />
                  
                  {/* Total allowed reference line */}
                  <ReferenceLine 
                    y={stats.contract_total_allowed} 
                    stroke="#ef4444" 
                    strokeDasharray="3 3"
                    label={{ value: "Limite contrat", fill: '#ef4444', fontSize: 12 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Readings Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Historique des relevés</CardTitle>
        </CardHeader>
        <CardContent>
          {readings.length === 0 ? (
            <div className="text-center py-8">
              <Gauge className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun relevé enregistré</p>
              <Button variant="outline" className="mt-3" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter le premier relevé
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Kilométrage</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {readings.map((reading, idx) => (
                  <TableRow key={reading.id}>
                    <TableCell>{formatDate(reading.reading_date)}</TableCell>
                    <TableCell className="font-medium font-mono">
                      {formatMileage(reading.mileage)}
                      {idx < readings.length - 1 && (
                        <span className="text-xs text-slate-400 ml-2">
                          (+{(reading.mileage - readings[idx + 1].mileage).toLocaleString("fr-FR")} km)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {MILEAGE_SOURCE_LABELS[reading.source] || reading.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 max-w-[200px] truncate">
                      {reading.notes || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteDialog({ open: true, reading })}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau relevé kilométrique</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date du relevé *</Label>
              <DatePickerFr
                value={form.reading_date}
                onChange={(v) => handleChange("reading_date", v)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Kilométrage *</Label>
              <Input
                type="number"
                min="0"
                value={form.mileage}
                onChange={(e) => handleChange("mileage", e.target.value)}
                placeholder="Ex: 45230"
                className="font-mono"
                data-testid="mileage-input"
              />
              {vehicle.current_mileage && (
                <p className="text-xs text-slate-500">
                  Dernier relevé: {formatMileage(vehicle.current_mileage)}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => handleChange("source", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MILEAGE_SOURCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Input
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Ex: Relevé hebdomadaire"
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="force_override"
                checked={form.force_override}
                onCheckedChange={(checked) => handleChange("force_override", checked)}
              />
              <label htmlFor="force_override" className="text-sm text-slate-600 cursor-pointer">
                Forcer la correction (si km inférieur au dernier relevé)
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-mileage">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, reading: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce relevé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le relevé du {formatDate(deleteDialog.reading?.reading_date)} ({formatMileage(deleteDialog.reading?.mileage)}) sera supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
